import { Badge, Box, Chip, Stack, Tooltip } from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FlagIcon from '@mui/icons-material/Flag';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import { EsgRecord, EsgRecordUpdateResponse } from '../../../types/syntiro_honey';
import ConfidenceBandChip from './ConfidenceBandChip';
import ConfirmMappingButton from './ConfirmMappingButton';
import MappingStatusChip from './MappingStatusChip';
import MetricSuggestionPicker from './MetricSuggestionPicker';
import RecordStatusIcon from './RecordStatusIcon';
import { formatDisplayValue } from './value_format';
import { formatProvenance } from './provenance';
import { crossFileFlag, isMappedFromLearnedRule } from './validation_flags';
import { resolveRecordEntity } from './record_entity';

interface RecordCellProps {
  /** The record of the row being rendered. */
  record: EsgRecord;
}

/**
 * Grid cell for the raw extracted label. A plain DataGrid text column clipped it
 * with an ellipsis and no way to read the rest, on the one field the reviewer is
 * matching against the metric beside it.
 * @param {EsgRecord} record The record of the row being rendered.
 * @returns {JSX.Element}
 */
export function RawLabelCell({ record }: RecordCellProps) {
  return (
    <Tooltip title={record.rawLabel}>
      {/* span carries the ref Tooltip needs; the typography wrapper cannot */}
      <span style={{ overflow: 'hidden', display: 'block' }}>
        <BodyText noWrap>{record.rawLabel}</BodyText>
      </span>
    </Tooltip>
  );
}

interface MetricCellProps extends RecordCellProps {
  /** Whether this row is currently expanded to show the suggestion picker. */
  expanded?: boolean;
  /** Toggle the inline suggestion picker for this row (without opening the drawer). */
  onToggleExpand?: () => void;
  /** Called after a suggestion is assigned, so the grid can refresh + collapse. */
  onAssigned?: () => void;
  /** Open the full detail drawer (the "search all metrics" fallback). */
  onOpenDetail?: () => void;
}

/**
 * Grid cell for the mapped metric: the catalogue name served on the record
 * (falling back to a candidate match, then the raw metric id) when mapped, or
 * an "unmapped" chip with a badge showing how many mapping candidates await
 * disambiguation.
 * @param {EsgRecord} record The record of the row being rendered.
 * @returns {JSX.Element}
 */
export function MetricCell({
  record,
  expanded = false,
  onToggleExpand,
  onAssigned,
  onOpenDetail
}: MetricCellProps) {
  const candidates = record.mappingCandidates ?? [];
  if (record.metricId !== null) {
    const matched = candidates.find((candidate) => candidate.metricId === record.metricId);
    const displayName = record.metricName ?? matched?.name ?? record.metricId;
    const learnedTitle = 'Mapped automatically from a rule your team taught';
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: 'hidden' }}>
        {/* The learning loop's payoff, visible at the moment it happens: this metric
            came from the client's own prior confirmation, not an AI guess. titleAccess
            carries the tooltip to screen readers (the ValueCell tick pattern). */}
        {isMappedFromLearnedRule(record.validationFlags) && (
          <Tooltip title={learnedTitle}>
            <SchoolOutlinedIcon
              color="success"
              sx={{ fontSize: 16, flexShrink: 0 }}
              titleAccess={learnedTitle}
            />
          </Tooltip>
        )}
        {/* Hover shows the full metric NAME (the cell truncates it) rather than the opaque
            metric id — the id is meaningless to a reviewer (Maxine #4). */}
        <Tooltip title={displayName}>
          {/* span carries the ref Tooltip needs; the typography wrapper cannot */}
          <span style={{ overflow: 'hidden' }}>
            <BodyText noWrap>{displayName}</BodyText>
          </span>
        </Tooltip>
      </Stack>
    );
  }
  // Unmapped: the badge is the EXPANDER (stops propagation so the row click that opens
  // the detail drawer still works elsewhere on the row). Expanding reveals the top-3
  // suggestion picker inline; the row grows via the grid's dynamic row height.
  const canSuggest = candidates.length > 0 && onToggleExpand !== undefined;
  return (
    // The badge is anchored OUTSIDE the chip's own box, so this cell must not clip:
    // `overflow: hidden` cut the candidate count in half (Maxine 2026-08-03 ⑥).
    // Right padding reserves room for it inside the cell instead.
    <Box sx={{ width: '100%', pr: 1.5 }}>
      <Badge badgeContent={candidates.length} color="warning">
        <Chip
          label="unmapped"
          size="small"
          variant="outlined"
          color="warning"
          clickable={canSuggest}
          onClick={
            canSuggest
              ? (event) => {
                  event.stopPropagation();
                  onToggleExpand?.();
                }
              : undefined
          }
        />
      </Badge>
      {expanded && candidates.length > 0 && (
        // Stop propagation so interacting with the picker never opens the detail drawer.
        <Box onClick={(event) => event.stopPropagation()}>
          <MetricSuggestionPicker
            record={record}
            onAssigned={() => onAssigned?.()}
            onSearchAll={() => onOpenDetail?.()}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * Grid cell for the record value: the qualitative verdict for compliance
 * records, otherwise the normalised value + canonical unit with the immutable
 * original ("Client Value") as a secondary line.
 * @param {EsgRecord} record The record of the row being rendered.
 * @returns {JSX.Element}
 */
export function ValueCell({ record }: RecordCellProps) {
  // The value-trust signal at a glance: a correct number reads as trusted even when
  // the metric/unit still needs work (the whole point of the two-signal split). The
  // tooltip names the actual source location — "trusted" is a provable claim about
  // a specific cell, not a vibe.
  const source = formatProvenance(record.rawPayload);
  // titleAccess doubles the tooltip copy as the icon's accessible name — the
  // trust signal must reach screen readers, not only mouse hover.
  const trustedTitle =
    source === null
      ? 'Value read from the source cell — trusted'
      : `Value read from ${source} — trusted`;
  const tick = record.valueTrusted ? (
    <Tooltip title={trustedTitle}>
      <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16 }} titleAccess={trustedTitle} />
    </Tooltip>
  ) : (
    <Tooltip title="Value could not be verified — please check it">
      <ErrorOutlineIcon
        color="warning"
        sx={{ fontSize: 16 }}
        titleAccess="Value could not be verified — please check it"
      />
    </Tooltip>
  );

  if (record.complianceValue !== null) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {tick}
        <Chip label={record.complianceValue} size="small" variant="outlined" />
      </Stack>
    );
  }
  // No normalised value yet (e.g. unmapped): lead with the client original so the row
  // does not read as "no data" when the source value is right there.
  const body =
    record.value === null && record.rawValue !== null ? (
      <Box sx={{ lineHeight: 1.3, overflow: 'hidden' }}>
        <BodyText noWrap>
          {formatDisplayValue(record.rawValue)} {record.rawUnit ?? ''}
        </BodyText>
        <CaptionText noWrap>client value — not normalised yet</CaptionText>
      </Box>
    ) : (
      <Box sx={{ lineHeight: 1.3, overflow: 'hidden' }}>
        <BodyText noWrap>
          {formatDisplayValue(record.value)} {record.canonicalUnit ?? ''}
        </BodyText>
        <CaptionText noWrap>
          Client: {formatDisplayValue(record.rawValue)} {record.rawUnit ?? ''}
        </CaptionText>
      </Box>
    );
  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ overflow: 'hidden' }}>
      <Box sx={{ mt: 0.25 }}>{tick}</Box>
      {body}
    </Stack>
  );
}

/**
 * Grid cell for the resolved entity: the most specific level (site → subdivision →
 * division) with the country as a secondary line, or an em dash when unresolved.
 * @param {EsgRecord} record The record of the row being rendered.
 * @returns {JSX.Element}
 */
export function EntityCell({ record }: RecordCellProps) {
  const entity = resolveRecordEntity(record);
  if (entity === null) {
    return <CaptionText>—</CaptionText>;
  }
  // Show the resolved LEVEL (e.g. "Division") so a record attached above site level
  // reads as intentionally attributed, not as a record with a missing site.
  const secondary =
    record.countryName !== null ? `${entity.level} · ${record.countryName}` : entity.level;
  return (
    <Box sx={{ lineHeight: 1.3, overflow: 'hidden' }}>
      {/* Site names truncate constantly ("Ngala Tented Safa…") and, without this,
          the full name was unrecoverable without opening the record. Same pattern
          the metric cell already uses; the span carries the ref Tooltip needs,
          which the typography wrapper cannot. */}
      <Tooltip title={entity.name}>
        <span style={{ overflow: 'hidden', display: 'block' }}>
          <BodyText noWrap>{entity.name}</BodyText>
        </span>
      </Tooltip>
      <CaptionText noWrap>{secondary}</CaptionText>
    </Box>
  );
}

/**
 * The orthogonal record markers: follow-up flag and specialist-requested
 * indicator. No longer its own grid column — two rarely-set icons did not earn a
 * 90px column plus a header, and the width was coming out of the Raw label and
 * Metric columns the review decision actually depends on. They now sit beside the
 * status chip, which is where a reviewer already looks for a record's state.
 * @param {EsgRecord} record The record of the row being rendered.
 * @returns {JSX.Element}
 */
export function MarkersCell({ record }: RecordCellProps) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {record.needsFollowUp && (
        <Tooltip title="Needs follow-up">
          <FlagIcon sx={{ fontSize: 16 }} color="action" titleAccess="Needs follow-up" />
        </Tooltip>
      )}
      {record.specialistRequested && (
        <Tooltip title="EP-specialist validation requested">
          <SupportAgentIcon
            sx={{ fontSize: 16 }}
            color="action"
            titleAccess="EP-specialist validation requested"
          />
        </Tooltip>
      )}
    </Stack>
  );
}

interface TrustCellProps extends RecordCellProps {
  /** Viewer mode: render the signals without the confirm affordance. */
  readOnly: boolean;
  /** Called with the updated record after an inline confirm succeeds. */
  onConfirmed: (updated: EsgRecordUpdateResponse) => void;
}

/**
 * The two trust signals in ONE column: how the metric was arrived at (chip, with
 * its confirm action) over the system-computed confidence (compact percentage).
 *
 * They were two columns of ~330px combined, which is a third of the width the two
 * fields under review were being truncated out of. Stacking them reclaims that
 * without collapsing the signals: DECISIONS #38 requires both to stay separately
 * readable — mapping answers "is the metric right", confidence answers "is the
 * whole record right", and a single blended score would hide a sentinel or unit
 * problem behind an exact match. Two lines, two answers, one column.
 * @param {TrustCellProps} props The record plus the confirm wiring.
 * @returns {JSX.Element}
 */
export function TrustCell({ record, readOnly, onConfirmed }: TrustCellProps) {
  const collision = crossFileFlag(record.validationFlags);
  return (
    // ONE LINE, in the order the decision runs: what the mapping is, how much the
    // system trusts the record, then the action that settles it. Stacking the score
    // under the chip made it read as a caption on the mapping rather than as the
    // second, independent signal it is (#38).
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <MappingStatusChip status={record.mappingStatus} approved={record.status === 'APPROVED'} />
      <ConfidenceBandChip
        confidence={record.overallConfidence}
        approved={record.status === 'APPROVED'}
        compact
      />
      {/* A cross-file collision must be visible ON THE ROW: it usually rides on an
          EXACT, high-confidence record, so without this chip the row reads as plain
          green "approve me" and the warning hides in the detail modal (Maxine R5 ③).
          A conflict (same datapoint, DIFFERENT value) is filled solid red rather than
          outlined: it means one of the two reported numbers is wrong, and an outlined
          chip proved too easy to miss in a row of outlined chips. */}
      {collision !== null && (
        <Tooltip
          title={
            collision === 'conflict'
              ? 'Disagrees with another file — open the record for details'
              : 'Already reported in another file — open the record for details'
          }
        >
          <Chip
            size="small"
            variant={collision === 'conflict' ? 'filled' : 'outlined'}
            color={collision === 'conflict' ? 'error' : 'warning'}
            label={collision === 'conflict' ? 'Conflicts' : 'Duplicate'}
          />
        </Tooltip>
      )}
      {/* A SUGGESTED mapping carries its confirm action right beside the signals that
          justify it: one click promotes it to a human-verified mapping AND teaches
          the per-client alias, so the same label maps automatically next upload. */}
      {!readOnly && <ConfirmMappingButton record={record} onConfirmed={onConfirmed} />}
    </Stack>
  );
}

interface StatusCellProps extends RecordCellProps {
  /**
   * The file the review is scoped to, if any. A record whose own `fileId` differs
   * came from an earlier upload in this workbook's lineage (carried over
   * unchanged), so the row marks it — otherwise a re-upload's locked rows look
   * unexplained.
   */
  viewedFileId?: string;
}

/**
 * Review status, plus the markers and the carried-over tag that used to occupy
 * their own columns. Grouping them is what a reviewer reads as one question —
 * "what state is this row in?" — so they belong in one region (proximity), and it
 * frees ~180px for the label and metric.
 * @param {StatusCellProps} props The record and the scoped file id.
 * @returns {JSX.Element}
 */
export function StatusCell({ record, viewedFileId }: StatusCellProps) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <RecordStatusIcon
        status={record.status}
        approvedById={record.approvedById}
        approvedAt={record.approvedAt}
        approvedByName={record.approvedByName}
      />
      <MarkersCell record={record} />
      {viewedFileId !== undefined && record.fileId !== viewedFileId && (
        <Tooltip title="Carried over unchanged from an earlier upload of this workbook">
          <CallSplitIcon
            sx={{ fontSize: 16 }}
            color="action"
            titleAccess="Carried over unchanged from an earlier upload of this workbook"
          />
        </Tooltip>
      )}
    </Stack>
  );
}
